import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import DialogActions from '@material-ui/core/DialogActions';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import DialogContentText from '@material-ui/core/DialogContentText';
import Dialog from '@afosto/app-components-react/Dialog';
import CurrencyField from '@afosto/app-components-react/CurrencyField';
import formatCurrency from '@afosto/utils/formatCurrency';
import { FormattedMessage } from 'react-intl';
import translations from './translations';
import useIntl from '@afosto/utils-react/intl/useIntl';

const RefundDialog = ({ open, onClose, onRefund, transaction }) => {
  const intl = useIntl();
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [amountToRefund, setAmountToRefund] = useState(null);
  const filteredPayments = (transaction?.payments ?? []).filter(payment => !payment?.is_refund);

  const handleChange = (e, value) => {
    setAmountToRefund(value);
  };

  const handleRefund = e => {
    e.preventDefault();
    if (onRefund && typeof onRefund === 'function') {
      onRefund(selectedPayment?.id, amountToRefund);
    }
  };

  useEffect(() => {
    let isMounted = true;

    if (isMounted) {
      setAmountToRefund(selectedPayment?.amount_paid ?? null);
    }

    return () => {
      isMounted = false;
    };
  }, [selectedPayment]);

  useEffect(() => {
    let isMounted = true;

    if (isMounted && filteredPayments.length === 1) {
      const [firstPayment] = filteredPayments;
      setSelectedPayment(firstPayment);
    }

    return () => {
      isMounted = false;
    };
  }, [open, filteredPayments]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      onExited={() => setSelectedPayment(null)}
    >
      <DialogTitle>
        <FormattedMessage {...translations.title} />
      </DialogTitle>
      {!selectedPayment && (
        <>
          <DialogContent>
            <DialogContentText>
              <FormattedMessage {...translations.choosePayment} />
            </DialogContentText>
          </DialogContent>
          <List>
            {filteredPayments.map(payment => (
              <ListItem button key={payment?.id} onClick={() => setSelectedPayment(payment)}>
                <Box
                  flex={1}
                  px={1}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <ListItemText
                    primary={
                      <FormattedMessage
                        id={`paymentMethods.${payment?.provider_code?.toLowerCase()}.${
                          payment.method_code
                        }`}
                        description={`Translation of cell value ${payment.method_code}`}
                        defaultMessage={payment.method_code}
                      />
                    }
                    secondary={
                      <FormattedMessage
                        id={`paymentMethods.providers.${payment.provider_code.toLowerCase()}`}
                        description={`Translation of cell value ${payment.provider_code}`}
                        defaultMessage={payment.provider_code}
                      />
                    }
                  />
                  <ListItemText
                    primary={formatCurrency((payment?.amount_paid ?? 0) / 100)}
                    primaryTypographyProps={{
                      align: 'right',
                    }}
                  />
                </Box>
              </ListItem>
            ))}
          </List>
        </>
      )}
      {selectedPayment && (
        <form onSubmit={handleRefund}>
          <DialogContent>
            <DialogContentText>
              <FormattedMessage {...translations.chooseAmountToRefund} />
            </DialogContentText>
            <CurrencyField
              label={intl.formatMessage(translations.fields.amount)}
              value={amountToRefund}
              onChange={handleChange}
              name="amountToRefund"
            />
            {amountToRefund !== transaction?.amount_paid && (
              <Box mb={2}>
                <Button
                  color="primary"
                  size="small"
                  onClick={() => setAmountToRefund(transaction?.amount_paid)}
                >
                  <FormattedMessage {...translations.enterTransactionTotal} />
                </Button>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button variant="outlined" color="primary" onClick={onClose}>
              <FormattedMessage {...translations.actions.cancel} />
            </Button>
            {filteredPayments.length > 1 && (
              <Button variant="outlined" color="primary" onClick={() => setSelectedPayment(null)}>
                <FormattedMessage {...translations.actions.otherPayment} />
              </Button>
            )}
            <Box ml="auto !important">
              <Button variant="contained" color="primary" type="submit">
                <FormattedMessage {...translations.actions.refund} />
              </Button>
            </Box>
          </DialogActions>
        </form>
      )}
    </Dialog>
  );
};

RefundDialog.propTypes = {
  onClose: PropTypes.func,
  onRefund: PropTypes.func,
  open: PropTypes.bool,
  transaction: PropTypes.object,
};

RefundDialog.defaultProps = {
  onClose: undefined,
  onRefund: undefined,
  open: false,
  transaction: null,
};

export default RefundDialog;
